import React from 'react'
import { observer } from 'mobx-react-lite'
import Stack from '@mui/material/Stack'

import {
  ANALYTICS_EVENTS,
  ConnectDrawer,
  EmptyState,
  Loader,
  useAccountStore,
  useAppConfig,
  useDebtStore,
  useEvent,
  useGlobalCopyStore,
  useGlobalUiStore,
  useGoalStore,
  useScreenSize,
  useWidgetLoadTimer,
  WidgetContainer,
} from '../common'
import { AccountType, WidgetProps } from '../core'

import SnowballCard from './components/SnowballCard'
import SnowballDrawer from './components/SnowballDrawer'
import DebtDrawer from './components/DebtDrawer'
import PaydownDrawer from './components/PaydownDrawer'
import { DebtsChart } from './components/DebtsChart'
import { DebtsTable } from './components/DebtsTable'
import { DebtsPriorityAction } from './components/actions/DebtsPriorityAction'
import { buildEmptyStateProps, transformDebtsChartData } from '../core/utils/DebtUtils'
import { useAccountUiStore } from '../accounts'
import { useDebtsUiStore } from './hooks/useDebtsUiStore'
import { PaydownButton } from './components/debtspaydown/PaydownButton'

const DebtsWidget: React.FC<WidgetProps> = ({ onBackClick, onMenuClick, sx }) => {
  const { isDesktop, isMobile } = useScreenSize()
  const { connect: connectCopy, debts: copy } = useGlobalCopyStore()
  const { onEvent } = useEvent()
  const {
    config: { show_connections_widget_in_master },
  } = useAppConfig()
  const { isInitialized } = useGlobalUiStore()
  const { setSelectedAccount } = useAccountUiStore()
  const { selectedDebtPriority, setSelectedDebtChartData } = useDebtsUiStore()

  const {
    isAccountDataLoaded,
    loadAccountData,
    detailedAccounts,
    visibleDebtAccounts: accounts,
  } = useAccountStore()
  const { isAllGoalDataLoaded, loadGoalData, monthlyCashFlowProfile } = useGoalStore()
  const { debts } = useDebtStore()

  const [isDebtDrawerOpen, setIsDebtDrawerOpen] = React.useState(false)
  const [isPaydownDrawerOpen, setIsPaydownDrawerOpen] = React.useState(false)
  const [isSnowballDrawerOpen, setIsSnowballDrawerOpen] = React.useState(false)
  const [hoveredDebtGuid, setHoveredDebtGuid] = React.useState('')
  const [showConnectWidget, setShowConnectWidget] = React.useState(false)

  useWidgetLoadTimer({
    widgetName: 'DebtsWidget',
    isLoaded: isAccountDataLoaded && isAllGoalDataLoaded,
  })

  const debtsChartData = React.useMemo(() => {
    const extraPaydown = monthlyCashFlowProfile?.extra_payment ?? 0
    return transformDebtsChartData(debts, selectedDebtPriority, extraPaydown)
  }, [debts, selectedDebtPriority, monthlyCashFlowProfile])

  React.useEffect(() => {
    if (!isAccountDataLoaded) {
      loadAccountData().finally()
    }
    onEvent(ANALYTICS_EVENTS.DEBTS_VIEW)
  }, [])

  React.useEffect(() => {
    if (isInitialized) {
      loadGoalData().finally()
    }
  }, [isInitialized])

  const onHoverDebt = (guid: string) => setHoveredDebtGuid(guid)

  const onClickDebt = () => {
    const account = accounts.find((a) => a.guid === hoveredDebtGuid)
    const chartData = debtsChartData.find((d) => d.guid === hoveredDebtGuid)
    if (account) setSelectedAccount(account)
    if (chartData) setSelectedDebtChartData(chartData)
    setIsDebtDrawerOpen(true)
  }

  const onClickAccountFilter = () => onEvent(ANALYTICS_EVENTS.DEBTS_CLICK_FILTER)

  const onClickSnowballCta = () => {
    setIsSnowballDrawerOpen(true)
    onEvent(ANALYTICS_EVENTS.DEBTS_CLICK_SNOWBALL_CTA)
  }

  const onClickPaydown = () => {
    setIsPaydownDrawerOpen(true)
    onEvent(ANALYTICS_EVENTS.DEBTS_CLICK_PAYDOWN)
  }

  if (!isInitialized || !isAccountDataLoaded || !isAllGoalDataLoaded) {
    return <Loader />
  }

  const hasNoAccounts = detailedAccounts.length === 0
  const hasNoDebtAccounts = accounts.length === 0
  const hasNoDebts = debts.length === 0
  const showEmptyState = hasNoAccounts || hasNoDebtAccounts || hasNoDebts

  const emptyStateProps = buildEmptyStateProps(copy, show_connections_widget_in_master, () =>
    setShowConnectWidget(true),
  )

  return (
    <WidgetContainer
      actions={[<DebtsPriorityAction disabled={showEmptyState} key="prioritize-debts" />]}
      allowedAccountTypes={[
        AccountType.CREDIT_CARD,
        AccountType.LOAN,
        AccountType.MORTGAGE,
        AccountType.LINE_OF_CREDIT,
        AccountType.CHECKING_LINE_OF_CREDIT,
      ]}
      hasDivider={true}
      onAccountsFilterClick={onClickAccountFilter}
      onBackClick={onBackClick}
      onMenuClick={onMenuClick}
      slotComponent={
        <PaydownButton disabled={showEmptyState} key="extra-paydown" onClick={onClickPaydown} />
      }
      sx={sx}
      title={copy.title}
    >
      <Stack sx={{ pt: 48, px: isDesktop ? '48px' : '24px' }}>
        {showEmptyState && (
          <EmptyState
            header={emptyStateProps.header}
            icon="note_stack"
            onClick={emptyStateProps.onClickHandler}
            primaryButton={emptyStateProps.primaryButton}
            subText={emptyStateProps.description}
            sx={{ maxWidth: 400 }}
          />
        )}
        {!showEmptyState && (
          <>
            <Stack
              sx={{
                alignItems: isMobile ? 'flex-start' : 'flex-end',
                flexDirection: isMobile ? 'column' : 'row',
                mx: isMobile ? '-12px' : 0,
              }}
            >
              <DebtsChart
                debts={debtsChartData}
                height={isMobile ? 250 : undefined}
                hoveredDebtGuid={hoveredDebtGuid}
                onClickArea={onClickDebt}
                onHoverArea={onHoverDebt}
                sx={{ mr: isMobile ? '4px' : '-48px' }}
              />
              <SnowballCard
                debts={debtsChartData}
                onClickCta={onClickSnowballCta}
                sx={{ mb: isMobile ? '0px' : '64px' }}
              />
            </Stack>
            <Stack sx={{ mx: isMobile ? '-24px' : 0 }}>
              <DebtsTable
                debts={debtsChartData}
                hoveredDebtGuid={hoveredDebtGuid}
                onClickRow={onClickDebt}
                onHoverRow={onHoverDebt}
              />
            </Stack>
          </>
        )}
      </Stack>
      <SnowballDrawer
        isOpen={isSnowballDrawerOpen}
        onClose={() => setIsSnowballDrawerOpen(false)}
      />
      <DebtDrawer isOpen={isDebtDrawerOpen} onClose={() => setIsDebtDrawerOpen(false)} />
      <PaydownDrawer isOpen={isPaydownDrawerOpen} onClose={() => setIsPaydownDrawerOpen(false)} />
      <ConnectDrawer
        onClose={() => setShowConnectWidget(false)}
        showConnectWidget={showConnectWidget}
        title={connectCopy.mini_title}
      />
    </WidgetContainer>
  )
}

export default observer(DebtsWidget)
